import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import { db } from '../../firebase/firebase';
import { Room } from '@shared/types';

interface RoomCardProps {
  room: Room;
}

export function RoomCard({ room }: RoomCardProps) {
  const [liveTableCount, setLiveTableCount] = useState<number>(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!room.id) return;

    // Listen to tables for this specific room
    const tablesQuery = query(
      collection(db, 'rooms', room.id, 'tables'),
      where('status', 'in', ['Open', 'Running'])
    );

    const unsubscribe = onSnapshot(tablesQuery, (snapshot) => {
      const totalTables = snapshot.docs.reduce((sum, doc) => {
        const tableData = doc.data();
        return sum + (tableData.number_of_tables || 0);
      }, 0);
      setLiveTableCount(totalTables);
      setLoading(false);
    }, (error) => {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error fetching live table count:', error);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, [room.id]);

  return (
    <div className="card card-hover">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <h3 className="text-xl font-semibold text-gray-900 mb-2">
            {room.name}
          </h3>
          <p className="text-gray-600 mb-4">{room.address}</p>
          {room.phone && (
            <p className="text-sm text-gray-500 mb-4">
              📞 {room.phone}
            </p>
          )}
        </div>
      </div>
      
      <div className="flex items-center justify-between">
        <div className="text-sm text-gray-500">
          {loading ? (
            <span className="flex items-center">
              <div className="w-3 h-3 border border-gray-300 border-t-primary rounded-full animate-spin mr-2"></div>
              Loading...
            </span>
          ) : (
            <span className="flex items-center">
              <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
              Live Tables: {liveTableCount}
            </span>
          )}
        </div>
        <Link
          to={`/${room.id}/tables`}
          className="btn btn-primary"
        >
          Enter Room
        </Link>
      </div>
    </div>
  );
}
